import React, { useContext } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ElipsisCell from "../../../../../components/TableBox/Cells/EllipsisCell";
import ElipsisText from "../../../../../components/ElipsisText";
import Link from "../../../../../components/Link";
import Badge from "../../../../../components/Badge";
import { RowClassName, RouteParamsTypes, Modules, PermissionModulesEnum, PermissionActionsEnum } from "../../../../../enums";
import { TableContext } from "../../../../../contexts/TableContext";
import { AuthContext } from "../../../../../contexts/AuthContext";
import { VALIDATION_FIELD_MAPPING } from "../../../../../utils/appConstant";
import { formatDollarAmount, hasResourseAccess } from "../../../../../utils/methods";
import { ManagerStatementListItem } from "../../../../../interfaces/managerStatement.interface";

type BadgeVariant = 'success' | 'danger' | 'info' | 'warning' | 'primary' | 'secondary';

const STATUS_BADGE_VARIANT: Record<string, BadgeVariant> = {
  draft: 'secondary',
  ready_for_review: 'warning',
  need_clarification: 'danger',
  completed: 'info',
  paid: 'success',
};

interface RowProps {
  item: ManagerStatementListItem;
  className?: RowClassName;
  handleRemoveClick: (_id: number) => void;
}

const Row: React.FC<RowProps> = ({ item, className, handleRemoveClick }) => {
  const { t } = useTranslation();
  const { headCells } = useContext(TableContext);
  const { permissions, isSystemAdmin } = useContext(AuthContext);
  const navigate = useNavigate();

  const detailPath = `${Modules.reconciliation}${RouteParamsTypes.managerStatement}/${item.id}`;

  const hasDeleteAccess = hasResourseAccess(isSystemAdmin, permissions, { accessibleTo: [{ [PermissionModulesEnum.ManagerStatement]: [PermissionActionsEnum.Delete] }] });
  const canDelete = hasDeleteAccess && item.status === 'draft';

  // Bare <i> action icons carry only visual `btn` styling, not native focusability -
  // tabIndex + this Enter/Space handler are required for real keyboard navigation
  // (fe/ACCESSIBILITY.md §2 "no keyboard trap" / "keyboard navigation reaches all
  // interactive elements" - role="button" alone does not add either of these).
  const handleActionKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      navigate(detailPath);
    }
  };

  const statementNumberNode = (
    <Link
      text={item.statement_number}
      href={detailPath}
      onClick={() => navigate(detailPath)}
      className="h6 mb-0 text-decoration-underline hyperlink"
    />
  );

  const statusNode = item.status ? (
    <Badge
      text={item.status_label ?? item.status}
      variant={STATUS_BADGE_VARIANT[item.status] ?? 'secondary'}
    />
  ) : (
    <span>-</span>
  );

  const actionsNode = (
    <div className="d-flex align-items-sm-center justify-content-sm-start">
      <i
        className="ri-edit-box-line ri-20px btn btn-sm btn-icon btn-text-secondary waves-effect waves-light rounded-pill"
        role="button"
        tabIndex={0}
        aria-label={t('reconciliation.managerStatement.list.pageTexts.table.actions.edit')}
        onClick={() => navigate(detailPath)}
        onKeyDown={handleActionKeyDown}
      />
      <i
        className="ri-eye-line ri-20px btn btn-sm btn-icon btn-text-secondary waves-effect waves-light rounded-pill"
        role="button"
        tabIndex={0}
        aria-label={t('reconciliation.managerStatement.list.pageTexts.table.actions.preview')}
        onClick={() => navigate(detailPath)}
        onKeyDown={handleActionKeyDown}
      />
      <i
        className="ri-history-line ri-20px btn btn-sm btn-icon btn-text-secondary waves-effect waves-light rounded-pill"
        role="button"
        tabIndex={0}
        aria-label={t('reconciliation.managerStatement.list.pageTexts.table.actions.history')}
        onClick={() => navigate(detailPath)}
        onKeyDown={handleActionKeyDown}
      />
      {canDelete && (
        <i
          className="ri-delete-bin-line ri-20px btn btn-sm btn-icon btn-text-danger waves-effect waves-light rounded-pill"
          role="button"
          tabIndex={0}
          aria-label={t('reconciliation.managerStatement.list.pageTexts.table.actions.delete')}
          onClick={() => handleRemoveClick(item.id)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              handleRemoveClick(item.id);
            }
          }}
        />
      )}
    </div>
  );

  return (
    <tr className={className}>
      {headCells.map((headCell) => {
        if (headCell.id === VALIDATION_FIELD_MAPPING.statementNumber) {
          return <ElipsisCell key={headCell.id} width={headCell.width} node={statementNumberNode} />;
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.month) {
          return <ElipsisCell key={headCell.id} width={headCell.width} text={item.month_label} />;
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.statementDate) {
          return <ElipsisCell key={headCell.id} width={headCell.width} text={item.statement_date ?? '-'} />;
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.manager) {
          return (
            <ElipsisCell key={headCell.id} width={headCell.width}
              node={<ElipsisText width="100%" text={item.manager_name ?? '-'} />}
            />
          );
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.amount) {
          return (
            <td key={headCell.id} width={headCell.width}>
              <span className="fw-semibold">{formatDollarAmount(item.amount)}</span>
            </td>
          );
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.amountDue) {
          return (
            <td key={headCell.id} width={headCell.width}>
              <span className="fw-semibold">{formatDollarAmount(item.amount_due)}</span>
            </td>
          );
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.status) {
          return (
            <td key={headCell.id} width={headCell.width}>
              {statusNode}
            </td>
          );
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.actions) {
          return (
            <td key={headCell.id} width={headCell.width}>
              {actionsNode}
            </td>
          );
        }

        return <ElipsisCell key={headCell.id} width={headCell.width} text={(item as any)[headCell.id] ?? '-'} />;
      })}
    </tr>
  );
};

export default Row;
